import {
  parseControlMessage,
  parseSignalMessage,
  type CameraName,
  type ControlAck,
  type ControlFrame,
  type IceServer,
  type PublicConfig,
  type SignalMessage,
  type SystemStats,
} from '@digger/shared';

export interface TransportEvents {
  onState(state: string): void;
  onTrack(camera: CameraName, stream: MediaStream): void;
  onAck(ack: ControlAck): void;
  onControlOpen(): void;
  onControlClosed(): void;
  /** Another operator took over — do not fight them for the digger. */
  onDisplaced(): void;
  /** Agent config pushed over signaling (before the offer, every session). */
  onConfig?(config: PublicConfig): void;
  /** Pi telemetry pushed over signaling (relay mode). */
  onSystem?(stats: SystemStats): void;
}

export interface WebRtcTransportOptions {
  /** From /api/config in agent (LAN) mode; relay mode learns it over signaling. */
  initialConfig?: PublicConfig;
  /** Auth: resolved fresh before every (re)connect. May show the login
   *  overlay and wait — which naturally pauses the reconnect loop. */
  getToken?: () => Promise<string>;
  /**
   * The far end rejected our token (agent-side check). Return false when the
   * rejection is terminal — no fresh token can be obtained (a one-shot booked
   * drive token) — so the transport stops instead of re-sending the same
   * rejected token forever. Returning true/void keeps the reconnect loop
   * (which a login re-prompt naturally pauses).
   */
  onAuthRejected?: () => boolean | void;
}

export interface ControlTransport {
  /** Returns false when the channel can't take the frame (it is dropped —
   *  the next tick supersedes it and the agent deadman covers the gap). */
  sendFrame(frame: ControlFrame): boolean;
  start(): void;
  stop(): void;
}

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 5000;

/**
 * WebRTC operator transport: signaling over /ws/signal, media + control on
 * one RTCPeerConnection. The agent offers; we answer. Reconnects with
 * backoff after any failure — the digger is stopped by the agent's deadman
 * during every gap, so reconnecting is always safe.
 */
export class WebRtcTransport implements ControlTransport {
  private ws: WebSocket | undefined;
  private pc: RTCPeerConnection | undefined;
  private dc: RTCDataChannel | undefined;
  private cameras: Record<string, CameraName> = {};
  private attempts = 0;
  private running = false;
  private reconnectTimer: number | undefined;
  private config: PublicConfig | undefined;
  /** Relay-minted (short-lived TURN creds); preferred over config's list. */
  private relayIceServers: IceServer[] | undefined;

  constructor(
    private readonly events: TransportEvents,
    private readonly opts: WebRtcTransportOptions = {},
  ) {
    this.config = opts.initialConfig;
  }

  get peerConnection(): RTCPeerConnection | undefined {
    return this.pc;
  }

  /** mid → camera mapping from the agent's offer (for stats attribution). */
  get cameraMids(): Readonly<Record<string, CameraName>> {
    return this.cameras;
  }

  start(): void {
    this.running = true;
    this.connect();
  }

  stop(): void {
    this.running = false;
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.teardown();
  }

  sendFrame(frame: ControlFrame): boolean {
    if (!this.dc || this.dc.readyState !== 'open') return false;
    // bufferedAmount > 0 means SCTP is backed up; queueing control frames
    // behind it would deliver stale commands. Drop instead.
    if (this.dc.bufferedAmount > 0) return false;
    this.dc.send(JSON.stringify(frame));
    return true;
  }

  private connect(): void {
    if (!this.running) return;
    this.events.onState(this.attempts === 0 ? 'signaling' : 'reconnecting');
    void (async () => {
      let token: string | undefined;
      if (this.opts.getToken) {
        try {
          token = await this.opts.getToken(); // may await the login overlay
        } catch {
          this.scheduleReconnect('auth unavailable');
          return;
        }
      }
      if (!this.running) return;

      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${proto}://${location.host}/ws/signal`);
      this.ws = ws;

      ws.onopen = () => {
        this.send({ type: 'hello', role: 'operator', token });
      };
      ws.onmessage = (e) => {
        const msg = parseSignalMessage(String(e.data));
        if (msg) void this.handleSignal(msg);
      };
      ws.onclose = () => {
        if (this.ws === ws) this.scheduleReconnect('signaling closed');
      };
      ws.onerror = () => ws.close();
    })();
  }

  private async handleSignal(msg: SignalMessage): Promise<void> {
    switch (msg.type) {
      case 'offer': {
        this.cameras = msg.cameras;
        const pc = this.createPeerConnection();
        await pc.setRemoteDescription({ type: 'offer', sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        this.send({ type: 'answer', sdp: answer.sdp ?? '' });
        this.events.onState('connecting');
        break;
      }
      case 'ice':
        if (this.pc) {
          await this.pc
            .addIceCandidate({
              candidate: msg.candidate.candidate,
              sdpMid: msg.candidate.sdpMid ?? undefined,
              sdpMLineIndex: msg.candidate.sdpMLineIndex ?? undefined,
            })
            .catch(() => {});
        }
        break;
      case 'bye':
        if (msg.reason === 'replaced-by-new-operator') {
          this.running = false;
          this.teardown();
          this.events.onDisplaced();
        } else {
          this.scheduleReconnect(`bye: ${msg.reason}`);
        }
        break;
      case 'config':
        this.config = msg.config;
        this.events.onConfig?.(msg.config);
        break;
      case 'ice-servers':
        this.relayIceServers = msg.iceServers;
        break;
      case 'system':
        this.events.onSystem?.(msg.stats);
        break;
      case 'error':
        if (msg.message === 'unauthorized') {
          // Agent-side (or relay-side) token rejection. If the provider can
          // re-prompt, drop credentials and retry; if it's terminal (a
          // booked-slot token with no fallback), stop — retrying would just
          // re-send the same rejected token in a tight loop.
          const recoverable = this.opts.onAuthRejected?.();
          if (recoverable === false) {
            this.stop();
            this.events.onState('unauthorized');
            return;
          }
        }
        this.scheduleReconnect(`signal error: ${msg.message}`);
        break;
      default:
        break;
    }
  }

  /** Zero the dead-reckoned pose on the agent, over signaling. */
  sendPoseReset(): void {
    this.send({ type: 'pose-reset' });
  }

  private createPeerConnection(): RTCPeerConnection {
    this.pc?.close();
    const pc = new RTCPeerConnection({
      iceServers: (this.relayIceServers ?? this.config?.iceServers ?? []).map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    });
    this.pc = pc;

    pc.ontrack = (e) => {
      const camera = this.cameras[e.transceiver.mid ?? ''];
      if (!camera) return;
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      this.events.onTrack(camera, stream);
    };

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        this.send({
          type: 'ice',
          candidate: {
            candidate: e.candidate.candidate,
            sdpMid: e.candidate.sdpMid,
            sdpMLineIndex: e.candidate.sdpMLineIndex,
          },
        });
      }
    };

    pc.onconnectionstatechange = () => {
      this.events.onState(pc.connectionState);
      if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
        this.scheduleReconnect(`pc ${pc.connectionState}`);
      }
      if (pc.connectionState === 'connected') this.attempts = 0;
    };

    pc.ondatachannel = (e) => {
      this.dc = e.channel;
      this.dc.onopen = () => this.events.onControlOpen();
      this.dc.onclose = () => this.events.onControlClosed();
      this.dc.onmessage = (m) => {
        const parsed = parseControlMessage(String(m.data));
        if (parsed?.type === 'ack') this.events.onAck(parsed);
      };
    };

    return pc;
  }

  private send(msg: SignalMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private teardown(): void {
    this.events.onControlClosed();
    this.dc = undefined;
    this.pc?.close();
    this.pc = undefined;
    const ws = this.ws;
    this.ws = undefined;
    ws?.close();
  }

  private scheduleReconnect(why: string): void {
    if (!this.running || this.reconnectTimer !== undefined) return;
    this.teardown();
    this.attempts++;
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (this.attempts - 1));
    this.events.onState(`reconnecting (${why})`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined;
      this.connect();
    }, delay);
  }
}

/**
 * Debug transport for `transport: "mjpeg-ws"`: control over WebSocket,
 * video as <img> MJPEG streams (wired up in main.ts, not here).
 */
export class WsControlTransport implements ControlTransport {
  private ws: WebSocket | undefined;
  private running = false;
  private reconnectTimer: number | undefined;
  private attempts = 0;

  constructor(private readonly events: TransportEvents) {}

  start(): void {
    this.running = true;
    this.connect();
  }

  stop(): void {
    this.running = false;
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.ws?.close();
    this.ws = undefined;
  }

  sendFrame(frame: ControlFrame): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    if (this.ws.bufferedAmount > 0) return false; // never queue stale control
    this.ws.send(JSON.stringify(frame));
    return true;
  }

  private connect(): void {
    if (!this.running) return;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}/ws/control`);
    this.ws = ws;
    this.events.onState(this.attempts === 0 ? 'connecting' : 'reconnecting');

    ws.onopen = () => {
      this.attempts = 0;
      this.events.onState('connected');
      this.events.onControlOpen();
    };
    ws.onmessage = (e) => {
      const parsed = parseControlMessage(String(e.data));
      if (parsed?.type === 'ack') this.events.onAck(parsed);
    };
    ws.onclose = () => {
      this.events.onControlClosed();
      if (this.ws !== ws || !this.running) return;
      this.attempts++;
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (this.attempts - 1));
      this.events.onState('reconnecting');
      this.reconnectTimer = window.setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => ws.close();
  }
}
